import { useAppDeps } from '@features/app-deps-provider'
import { useSetRoute } from '@features/app-hooks'
import { useOrganization } from '@features/organizations'
import { STCopyShareableLinkDialog } from '@features/st-folder-viewer/st-copy-shareable-link-dialog'
import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-module'
import { STAppHeaderNav } from '@features/st-organizations'
import { platformModule } from '@features/st-pdf-viewer/platform-module'
import { STQuestionnairesSendPage } from '@features/st-questionnaire-send'
import { STQuestionnaireDownloadCSVPage } from '@features/st-questionnaire-send/st-questionnaire-download-csv-page'
import {
  BatchActionToolbar,
  BellIcon,
  Button,
  Dialog,
  DialogButtons,
  DownloadIcon,
  Modal,
  PencilSquareIcon,
  PlusIcon,
  SendIcon,
  TrashIcon
} from '@st/theme'
import { inflect } from '@st/util/inflect'
import { isEmpty } from '@st/util/json-value'
import { copyToClipboard } from '@util/clipboard'
import { ReactNode, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { clientFolderShareableLink, routes } from '~/features/routes'
import { SendClientOrganizerDialog } from '~/features/st-folder-viewer/send-client-organizer-dialog'
import { useProcess, useProcessState } from '~/store'
import { organizationBillingModule } from '../billing/organization-billing-module'
import { EditFolderTagsDialog } from '../st-folder-viewer/edit-folder-tags-dialog'
import { STEnrollInRemindersPage } from './st-enroll-in-reminders-page'
import { STFolderListFiltersBar } from './st-folder-list-filters-bar'
import { stFolderListFiltersModule } from './st-folder-list-filters-module'
import {
  BatchActionType,
  FolderRow,
  selAvailableBulkActions,
  selFinalFolderList,
  STFolderAction,
  stFolderListModule,
  unenrollFromReminders
} from './st-folder-list-module'
import { STFolderListTable } from './st-folder-list-table'
import { STToggleClientOrganizerSectionsDialog } from './st-toggle-client-organizer-sections'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { FolderListCommandPallette } from './folder-list-commands'
import { useDevMode } from '@st/react-util/dev-mode'

export function STFolderListPage() {
  const { socket, sdk } = useAppDeps()
  const organization = useOrganization()
  const isDevMode = useDevMode()

  const setRoute = useSetRoute()

  const platform = useProcess(platformModule)

  useProcess(organizationBillingModule, { organizationId: organization.id })

  const stFolderList = useProcess(stFolderListModule, { organizationId: organization.id })
  const stFolderListFilters = useProcess(stFolderListFiltersModule)

  const state = useProcessState(stFolderListModule, (s) => s)
  const availableBulkActions = useProcessState(stFolderListModule, selAvailableBulkActions)
  const filtersState = useProcessState(stFolderListFiltersModule, (s) => s)

  const [action, setAction] = useState<STFolderAction | undefined>()

  useKeyboardShortcuts(
    isDevMode
      ? {
          '$mod+k': () => setAction({ type: 'openCommandPalette' })
        }
      : {}
  )

  useEffect(() => {
    const unsub = socket.subscribe(`folder_list:${organization.id}`, {
      load: (e) => {
        stFolderList.send({ type: 'stateLoad', vsn: e.vsn, state: e.state })
      },
      diff: (e) => {
        stFolderList.send({ type: 'stateDiff', fromVsn: e.fromVsn, toVsn: e.toVsn, diff: e.diff })
      }
    })

    return () => unsub()
  }, [socket, organization.id, stFolderList])

  function onAction(action: STFolderAction) {
    switch (action.type) {
      case 'openFolder':
        setRoute({
          name: 'organization_folder',
          organizationSlug: organization.slug,
          folderId: action.folder.id
        })
        break
      case 'copyQuestionnaireLink':
        copyToClipboard(
          clientFolderShareableLink({
            organizationSlug: organization.slug,
            folderId: action.folderId
          })
        )
        platform.send({
          type: 'showSnackbar',
          message: 'Copied shareable link to clipboard'
        })
        break
      case 'previewQuestionnaire':
        window.open(
          routes.toPath({
            name: 'organization_preview_questionnaire',
            folderId: action.folderId,
            organizationSlug: organization.slug
          })
        )
        break
      case 'unarchiveFolder':
        sdk.send({ type: 'folders/unarchiveFolder', folderId: action.folder.id })
        platform.send({
          type: 'showSnackbar',
          message: `Unarchived binder for ${formatFolderEntityName(action.folder.entities)}`
        })
        break
      case 'unlockClientOrganizer':
        sdk.send({ type: 'folders/unlockClientOrganizer', folderId: action.folder.id })
        platform.send({
          type: 'showSnackbar',
          message: `Unlocked client organizer for ${formatFolderEntityName(action.folder.entities)}`
        })
        break
      case 'submitClientOrganizer':
        sdk.send({
          type: 'folders/submitClientOrganizer',
          folderId: action.folder.id,
          forceSubmit: true
        })
        platform.send({
          type: 'showSnackbar',
          message: `Submitted client organizer for ${formatFolderEntityName(
            action.folder.entities
          )}`
        })
        break
      case 'enableClientOrganizerSection':
        sdk.send({
          type: 'folders/enableClientOrganizerSection',
          folderId: action.folder.id,
          section: action.section
        })
        platform.send({
          type: 'showSnackbar',
          message: `Enabled questionnaire for ${formatFolderEntityName(action.folder.entities)}`
        })
        break
      case 'disableClientOrganizerSection':
        sdk.send({
          type: 'folders/disableClientOrganizerSection',
          folderId: action.folder.id,
          section: action.section
        })
        platform.send({
          type: 'showSnackbar',
          message: `Disabled questionnaire for ${formatFolderEntityName(action.folder.entities)}`
        })
        break
      default:
        setAction(action)
    }
  }

  // these are full screen dialogs so we will return early with just the dialogs
  // rather than rendering the underlying page
  if (action?.type == 'sendClientOrganizers') {
    return (
      <STQuestionnairesSendPage
        folderIds={action.folderIds}
        onClose={() => setAction(undefined)}
        onClickEnrollInReminders={(folderIds) =>
          setAction({ type: 'enrollInReminders', group: 'clientOrganizer', folderIds })
        }
      />
    )
  } else if (action?.type == 'enrollInReminders') {
    return (
      <STEnrollInRemindersPage folderIds={action.folderIds} onClose={() => setAction(undefined)} />
    )
  } else if (action?.type == 'downloadQuestionnaireLinks') {
    return (
      <STQuestionnaireDownloadCSVPage
        folderIds={action.folderIds}
        onClose={() => setAction(undefined)}
        onClickBack={() => setAction(undefined)}
      />
    )
  }

  switch (state.status) {
    case 'loading':
      return (
        <FolderListScaffold
          header={<STAppHeaderNav />}
          filters={
            <STFolderListFiltersBar
              filters={filtersState.filters}
              send={stFolderListFilters.send}
            />
          }
        >
          <></>
        </FolderListScaffold>
      )
    case 'loaded':
      if (isEmpty(state.folderListState!.folders)) {
        return (
          <FolderListScaffold title="Binders" header={<STAppHeaderNav />}>
            <FolderListEmptyState onAddClients={() => setRoute({ name: 'create_import' })} />
          </FolderListScaffold>
        )
      }

      const filteredFolders = selFinalFolderList(state, { stFolderListFilters: filtersState })

      return (
        <FolderListScaffold
          header={<STAppHeaderNav />}
          filters={
            <STFolderListFiltersBar
              filters={filtersState.filters}
              send={stFolderListFilters.send}
            />
          }
          buttons={
            <div className="ml-auto grid auto-cols-max grid-flow-col items-center gap-2">
              <Button
                leadingIcon={<DownloadIcon className="h-4 w-4" />}
                variant="default"
                onClick={() =>
                  onAction({
                    type: 'downloadQuestionnaireLinks',
                    folderIds: Object.values(state.folderListState!.folders).map((f) => f.id)
                  })
                }
              >
                Client organizer links
              </Button>
              <Button
                variant="default"
                leadingIcon={<PlusIcon className="h-4 w-4" />}
                onClick={() => setRoute({ name: 'create_import' })}
              >
                Binders
              </Button>
            </div>
          }
          batchActions={
            state.selectedFolderIds.length > 0 ? (
              <STFolderListBatchActions
                availableBulkActions={availableBulkActions}
                selectedFolderIds={state.selectedFolderIds}
                onAction={onAction}
                onClearSelection={() => stFolderList.send({ type: 'deselectAll' })}
              />
            ) : undefined
          }
        >
          <STFolderListTable
            items={filteredFolders}
            selectedFolderIds={state.selectedFolderIds}
            unreadDocumentCountsByFolderId={state.folderListState!.unreadDocumentCountsByFolderId}
            unexportedDocumentCountsByFolderId={
              state.folderListState!.unexportedDocumentCountsByFolderId
            }
            sortState={filtersState.sortState}
            send={stFolderList.send}
            onAction={onAction}
            onToggleSort={(column) => stFolderListFilters.send({ type: 'toggleSort', column })}
          />

          {match(action)
            .with({ type: 'openSendQuestionnaireDialog' }, ({ folder }) => (
              <Modal isOpen={true}>
                <SendClientOrganizerDialog
                  folderId={folder.id}
                  onClose={() => setAction(undefined)}
                />
              </Modal>
            ))
            .with({ type: 'openCopyShareableLinkDialog' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <STCopyShareableLinkDialog
                    folderId={folder.id}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .with({ type: 'deleteFolder' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <STDeleteFolderDialog folder={folder} onClose={() => setAction(undefined)} />
                </Modal>
              )
            })
            .with({ type: 'archiveFolder' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <STArchiveFolderDialog folder={folder} onClose={() => setAction(undefined)} />
                </Modal>
              )
            })
            .with({ type: 'editFolderTags' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <EditFolderTagsDialog
                    folderId={folder.id}
                    tagIds={folder.tagIds}
                    folderEntities={folder.entities}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .with({ type: 'deleteFolders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmDeleteFoldersDialog
                    folderIds={folderIds}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .with({ type: 'enableClientOrganizerSections' }, (action) => {
              return (
                <Modal isOpen={true}>
                  <STToggleClientOrganizerSectionsDialog
                    action={{
                      type: 'enableClientOrganizerSection',
                      section: action.section,
                      folderIds: action.folderIds
                    }}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .with({ type: 'disableClientOrganizerSections' }, (action) => {
              return (
                <Modal isOpen={true}>
                  <STToggleClientOrganizerSectionsDialog
                    action={{
                      type: 'disableClientOrganizerSection',
                      section: action.section,
                      folderIds: action.folderIds
                    }}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .with({ type: 'unenrollFromReminders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmUnenrollFromRemindersDialog
                    onClose={() => setAction(undefined)}
                    organizationId={organization.id}
                    folderIds={folderIds}
                  />
                </Modal>
              )
            })
            .with({ type: 'viewRemindersSchedule' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <Dialog
                    title="Automated Reminder"
                    subtitle={`Automated reminders begin on February 10th. A reminder will be sent to ${formatFolderEntityName(
                      folder.entities,
                      'client'
                    )} every Monday morning until they submit their questionnaire. You can view or modify the reminder email template in your Settings.`}
                    buttons={
                      <DialogButtons>
                        <Button
                          variant="default"
                          onClick={() => {
                            stFolderList.send(unenrollFromReminders(folder.id))
                            setAction(undefined)
                          }}
                        >
                          Unenroll from reminders
                        </Button>
                        <Button variant="primary" onClick={() => setAction(undefined)}>
                          OK
                        </Button>
                      </DialogButtons>
                    }
                  />
                </Modal>
              )
            })
            .with({ type: 'openCommandPalette' }, () => {
              return (
                <Modal isOpen={true} onClose={() => setAction(undefined)}>
                  <FolderListCommandPallette
                    organization={organization}
                    onClose={() => setAction(undefined)}
                  />
                </Modal>
              )
            })
            .otherwise(() => null)}
        </FolderListScaffold>
      )
  }
}
function FolderListScaffold({
  title,
  banner,
  filters,
  header,
  children,
  buttons,
  batchActions
}: {
  title?: string
  filters?: ReactNode
  banner?: ReactNode
  header: ReactNode
  children: ReactNode
  buttons?: ReactNode
  batchActions?: ReactNode
}) {
  return (
    <div className={'grid h-dvh min-w-[1200px] grid-rows-[auto_auto_minmax(0,1fr)_auto]'}>
      {header}

      <div className="flex flex-col">
        {banner}
        <div className="flex h-16 flex-row items-center px-20 py-12">
          {title && <h1 className="mr-6 text-2xl">{title}</h1>}
          {filters ?? <div />}
          {buttons ?? <div />}
        </div>
      </div>

      {children}

      {batchActions}
    </div>
  )
}

function STFolderListBatchActions({
  availableBulkActions,
  selectedFolderIds,
  onClearSelection,
  onAction
}: {
  availableBulkActions: BatchActionType[]
  selectedFolderIds: string[]
  onClearSelection: () => void
  onAction: (action: STFolderAction) => void
}) {
  const folderActions = availableBulkActions.filter((action) => action.group == 'folder')
  const clientOrganizerActions = availableBulkActions.filter(
    (action) => action.group == 'clientOrganizer'
  )

  return (
    <BatchActionToolbar
      selectionMessage={`${selectedFolderIds.length} selected`}
      onClearSelection={onClearSelection}
    >
      <BatchActionToolbar.Section title="Binder:">
        {folderActions.map((action) => {
          switch (action.type) {
            case 'deleteFolders':
              return (
                <BatchActionToolbar.Button
                  key={action.type}
                  icon={<TrashIcon className="size-4" />}
                  onClick={() => onAction(action)}
                >
                  Delete
                </BatchActionToolbar.Button>
              )
          }
        })}
      </BatchActionToolbar.Section>

      {clientOrganizerActions.length > 0 && <BatchActionToolbar.SectionDivider />}

      {clientOrganizerActions.length > 0 && (
        <BatchActionToolbar.Section title="Organizer:">
          {clientOrganizerActions.map((action) => {
            switch (action.type) {
              case 'enableClientOrganizerSections':
                return (
                  <BatchActionToolbar.Button
                    key={action.type}
                    icon={<PencilSquareIcon className="size-4" />}
                    onClick={() => onAction(action)}
                  >
                    Enable questionnaire
                  </BatchActionToolbar.Button>
                )
              case 'disableClientOrganizerSections':
                return (
                  <BatchActionToolbar.Button
                    key={action.type}
                    icon={<PencilSquareIcon className="size-4" />}
                    onClick={() => onAction(action)}
                  >
                    Disable questionnaire
                  </BatchActionToolbar.Button>
                )
              case 'sendClientOrganizers':
                return (
                  <BatchActionToolbar.Button
                    key={action.type}
                    icon={<SendIcon className="size-4" />}
                    onClick={() => onAction(action)}
                  >
                    Send to {inflect(selectedFolderIds.length, 'client')}
                  </BatchActionToolbar.Button>
                )
              case 'enrollInReminders':
                return (
                  <BatchActionToolbar.Button
                    key={action.type}
                    icon={<BellIcon className="size-4" />}
                    onClick={() => onAction(action)}
                  >
                    Enroll in reminders
                  </BatchActionToolbar.Button>
                )
              case 'unenrollFromReminders':
                return (
                  <BatchActionToolbar.Button
                    key={action.type}
                    icon={<BellIcon className="size-4" />}
                    onClick={() => onAction(action)}
                  >
                    Unenroll from reminders
                  </BatchActionToolbar.Button>
                )
            }
          })}
        </BatchActionToolbar.Section>
      )}
    </BatchActionToolbar>
  )
}

function STConfirmDeleteFoldersDialog({
  onClose,
  folderIds
}: {
  onClose: () => void
  folderIds: string[]
}) {
  const { sdk } = useAppDeps()
  const platform = useProcess(platformModule)

  return (
    <Dialog
      title="Delete binders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await Promise.all(
                folderIds.map((id) =>
                  sdk.send({
                    type: 'folders/deleteFolder',
                    folderId: id
                  })
                )
              )
              platform.send({
                type: 'showSnackbar',
                message: `Deleted ${folderIds.length} ${inflect(folderIds.length, 'binder')}`
              })
              onClose?.()
            }}
          >
            Confirm delete
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to delete {folderIds.length}{' '}
        {inflect(folderIds.length, 'binder')}.
      </p>
    </Dialog>
  )
}

function STArchiveFolderDialog({ folder, onClose }: { folder: FolderRow; onClose: () => void }) {
  const { sdk } = useAppDeps()
  const platform = useProcess(platformModule)
  const [deleting, setDeleting] = useState(false)

  return (
    <Dialog
      title="Archive binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true)
              try {
                await sdk.send({
                  type: 'folders/archiveFolder',
                  folderId: folder.id
                })
                platform.send({
                  type: 'showSnackbar',
                  message: `Archived binder for ${formatFolderEntityName(folder.entities)}`
                })
                onClose?.()
              } finally {
                setDeleting(false)
              }
            }}
          >
            {deleting ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to archive the binder for{' '}
        {formatFolderEntityName(folder.entities)}.
      </p>
    </Dialog>
  )
}

function STDeleteFolderDialog({ folder, onClose }: { folder: FolderRow; onClose: () => void }) {
  const { sdk } = useAppDeps()
  const platform = useProcess(platformModule)
  const [deleting, setDeleting] = useState(false)

  return (
    <Dialog
      title="Delete binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true)
              try {
                await sdk.send({
                  type: 'folders/deleteFolder',
                  folderId: folder.id
                })
                platform.send({
                  type: 'showSnackbar',
                  message: `Deleted binder for ${formatFolderEntityName(folder.entities)}`
                })
                onClose?.()
              } finally {
                setDeleting(false)
              }
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to delete the binder for{' '}
        {formatFolderEntityName(folder.entities)}.
      </p>
    </Dialog>
  )
}

function STConfirmUnenrollFromRemindersDialog({
  onClose,
  organizationId,
  folderIds
}: {
  onClose: () => void
  organizationId: string
  folderIds: string[]
}) {
  const { sdk } = useAppDeps()
  const platform = useProcess(platformModule)
  const [unenrolling, setUnenrolling] = useState(false)

  return (
    <Dialog
      title="Unenroll from reminders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={unenrolling}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={unenrolling}
            onClick={async () => {
              setUnenrolling(true)
              try {
                await sdk.send({
                  type: 'folders/bulkUnenrollClientOrganizersFromReminders',
                  organizationId: organizationId,
                  folderIds: folderIds
                })
                platform.send({
                  type: 'showSnackbar',
                  message: `Unenrolled ${folderIds.length} ${inflect(
                    folderIds.length,
                    'client'
                  )} from automated reminders`
                })
                onClose?.()
              } finally {
                setUnenrolling(false)
              }
            }}
          >
            {unenrolling ? 'Unenrolling...' : 'Confirm unenroll'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to unenroll {folderIds.length}{' '}
        {inflect(folderIds.length, 'client')} from automated reminders. You can re-enroll them at
        any time.
      </p>
    </Dialog>
  )
}

function FolderListEmptyState({ onAddClients }: { onAddClients: () => void }) {
  return (
    <div className="mx-auto mt-12 flex w-[650px] flex-col items-center gap-3">
      <div className="text-center text-xl text-gray-900">
        Generate your first StanfordTax Binder, directly from your tax software!
      </div>
      <div className="text-center text-base text-gray-700">
        Generate a binder for each return to keep track of client documents. Every binder comes with
        a client organizer which you can optionally send to clients. Information clients add to
        their organizer will automatically flow into the binder.
      </div>
      <Button
        variant="primary"
        leadingIcon={<PlusIcon className="h-4 w-4" />}
        className="mt-4"
        onClick={onAddClients}
      >
        Generate binders
      </Button>
    </div>
  )
}
